'use client'

import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
    MdArrowBack,
    MdEdit,
    MdVerified,
    MdPerson,
    MdLocalHospital,
    MdSend,
    MdCheckCircle,
    MdCancel, MdHistory
} from 'react-icons/md'
import { useAuth } from '@/store/hooks'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import {
    getAuthorization,
    submitAuthorization,
    approveAuthorization,
    denyAuthorization,
    expireAuthorization,
    Authorization,
    AUTHORIZATION_STATUS_LABELS,
    AUTHORIZATION_STATUS_COLORS,
    AUTHORIZATION_URGENCY_LABELS,
    AUTHORIZATION_URGENCY_COLORS,
} from '@/lib/api/billing/authorization-api'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import ConfirmModal from '@/components/confirm-modal'

export default function AuthorizationDetailPage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const { canUpdate, canDelete } = useResourcePermissions('billing', 'authorizations')

    const [authorization, setAuthorization] = useState<Authorization | null>(
        null
    )
    const [loading, setLoading] = useState(true)
    const [showApproveModal, setShowApproveModal] = useState(false)
    const [showDenyModal, setShowDenyModal] = useState(false)
    const [actionLoading, setActionLoading] = useState(false)
    const [showExpireModal, setShowExpireModal] = useState(false)

    const [approveData, setApproveData] = useState({
        authorizationNumber: '',
        operatorNumber: '',
        validUntil: '',
    })
    const [denyReason, setDenyReason] = useState('')

    useEffect(() => {
        if (id && user?.companyId) {
            loadAuthorization()
        }
    }, [id, user?.companyId])

    const loadAuthorization = async () => {
        if (!id) return

        setLoading(true)
        try {
            const response = await getAuthorization(parseInt(id, 10))
            if (response.success && response.result) {
                setAuthorization(response.result)
            } else {
                toast.error(response.error || 'Erro ao carregar autorização')
                navigate('/billing/authorizations')
            }
        } catch (error) {
            toast.error('Erro ao carregar autorização')
            navigate('/billing/authorizations')
        } finally {
            setLoading(false)
        }
    }

    const handleSubmit = async () => {
        if (!id) return

        setActionLoading(true)
        try {
            const response = await submitAuthorization(parseInt(id, 10))
            if (response.success) {
                toast.success('Autorização enviada para análise')
                loadAuthorization()
            } else {
                toast.error(response.error || 'Erro ao enviar autorização')
            }
        } catch (error) {
            toast.error('Erro ao enviar autorização')
        } finally {
            setActionLoading(false)
        }
    }

    const handleApprove = async () => {
        if (!id) return

        setActionLoading(true)
        try {
            const response = await approveAuthorization(parseInt(id, 10), {
                authorizationNumber:
                    approveData.authorizationNumber || undefined,
                operatorNumber: approveData.operatorNumber || undefined,
                validUntil: approveData.validUntil || undefined,
            })
            if (response.success) {
                toast.success('Autorização aprovada com sucesso')
                setShowApproveModal(false)
                setApproveData({
                    authorizationNumber: '',
                    operatorNumber: '',
                    validUntil: '',
                })
                loadAuthorization()
            } else {
                toast.error(response.error || 'Erro ao aprovar autorização')
            }
        } catch (error) {
            toast.error('Erro ao aprovar autorização')
        } finally {
            setActionLoading(false)
        }
    }

    const handleDeny = async () => {
        if (!id) return

        setActionLoading(true)
        try {
            const response = await denyAuthorization(
                parseInt(id, 10),
                denyReason || undefined
            )
            if (response.success) {
                toast.success('Autorização negada')
                setShowDenyModal(false)
                setDenyReason('')
                loadAuthorization()
            } else {
                toast.error(response.error || 'Erro ao negar autorização')
            }
        } catch (error) {
            toast.error('Erro ao negar autorização')
        } finally {
            setActionLoading(false)
        }
    }

    const handleExpire = () => {
        setShowExpireModal(true)
    }

    const confirmExpire = async () => {
        if (!id) return

        setActionLoading(true)
        try {
            const response = await expireAuthorization(parseInt(id, 10))
            if (response.success) {
                toast.success('Autorização marcada como expirada')
                loadAuthorization()
            } else {
                toast.error(response.error || 'Erro ao expirar autorização')
            }
        } catch (error) {
            toast.error('Erro ao expirar autorização')
        } finally {
            setActionLoading(false)
            setShowExpireModal(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando autorização...
                </p>
            </div>
        )
    }

    if (!authorization) {
        return null
    }

    const canSubmitAuth = authorization.status === 'AUTH_DRAFT'
    const canDecide = authorization.status === 'AUTH_PENDING'
    const canExpire =
        authorization.status === 'AUTH_APPROVED' && authorization.validUntil
    const canEditAuth = authorization.status === 'AUTH_DRAFT'

    return (
        <div className="flex flex-col gap-6 p-4">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/billing/authorizations')}
                        className="btn btn-ghost btn-sm"
                    >
                        <MdArrowBack className="text-lg" />
                    </button>
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-lg">
                        <MdVerified className="text-2xl text-white" />
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                                Autorização{' '}
                                {authorization.authorizationNumber || `#${authorization.id}`}
                            </h1>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                            <span
                                className={`badge ${AUTHORIZATION_STATUS_COLORS[authorization.status as keyof typeof AUTHORIZATION_STATUS_COLORS]}`}
                            >
                                {AUTHORIZATION_STATUS_LABELS[authorization.status as keyof typeof AUTHORIZATION_STATUS_LABELS]}
                            </span>
                            <span
                                className={`badge ${AUTHORIZATION_URGENCY_COLORS[authorization.urgency as keyof typeof AUTHORIZATION_URGENCY_COLORS]}`}
                            >
                                {AUTHORIZATION_URGENCY_LABELS[authorization.urgency as keyof typeof AUTHORIZATION_URGENCY_LABELS]}
                            </span>
                        </div>
                    </div>
                </div>
                {canUpdate && (
                    <div className="flex items-center gap-2 flex-wrap">
                        {canSubmitAuth && (
                            <button
                                onClick={handleSubmit}
                                className="btn btn-info btn-sm gap-2"
                                disabled={actionLoading}
                            >
                                <MdSend className="text-lg" />
                                Enviar para análise
                            </button>
                        )}
                        {canDecide && (
                            <>
                                <button
                                    onClick={() => setShowApproveModal(true)}
                                    className="btn btn-success btn-sm gap-2"
                                    disabled={actionLoading}
                                >
                                    <MdCheckCircle className="text-lg" />
                                    Aprovar
                                </button>
                                <button
                                    onClick={() => setShowDenyModal(true)}
                                    className="btn btn-error btn-sm gap-2"
                                    disabled={actionLoading}
                                >
                                    <MdCancel className="text-lg" />
                                    Negar
                                </button>
                            </>
                        )}
                        {canExpire && (
                            <button
                                onClick={handleExpire}
                                className="btn btn-warning btn-sm gap-2"
                                disabled={actionLoading}
                            >
                                <MdHistory className="text-lg" />
                                Expirar
                            </button>
                        )}
                        {canEditAuth && (
                            <button
                                onClick={() =>
                                    navigate(
                                        `/billing/authorizations/${id}/edit`
                                    )
                                }
                                className="btn btn-secondary btn-sm gap-2"
                            >
                                <MdEdit className="text-lg" />
                                Editar
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Info Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body p-4">
                        <div className="flex items-center gap-2 mb-2">
                            <MdPerson className="text-secondary" />
                            <span className="text-xs text-base-content/60 uppercase">
                                Paciente
                            </span>
                        </div>
                        <p className="font-semibold">
                            {authorization.client?.name || '-'}
                        </p>
                        <p className="text-sm text-base-content/60">
                            Carteirinha: {authorization.cardNumber || '-'}
                        </p>
                    </div>
                </div>
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body p-4">
                        <div className="flex items-center gap-2 mb-2">
                            <MdLocalHospital className="text-secondary" />
                            <span className="text-xs text-base-content/60 uppercase">
                                Convenio
                            </span>
                        </div>
                        <p className="font-semibold">
                            {authorization.healthPlan?.name || '-'}
                        </p>
                    </div>
                </div>
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body p-4">
                        <span className="text-xs text-base-content/60 uppercase">
                            Solicitação
                        </span>
                        <p className="font-semibold">
                            {authorization.requestDate
                                ? format(
                                    new Date(authorization.requestDate),
                                    'dd/MM/yyyy',
                                    { locale: ptBR }
                                )
                                : '-'}
                        </p>
                    </div>
                </div>
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body p-4">
                        <span className="text-xs text-base-content/60 uppercase">
                            Validade
                        </span>
                        <p className="font-semibold">
                            {authorization.validUntil
                                ? format(
                                    new Date(authorization.validUntil),
                                    'dd/MM/yyyy',
                                    { locale: ptBR }
                                )
                                : '-'}
                        </p>
                    </div>
                </div>
            </div>

            {/* Authorization Details */}
            {(authorization.authorizationNumber ||
                authorization.operatorNumber) && (
                    <div className="card bg-success/10 border border-success/30 shadow-sm">
                        <div className="card-body">
                            <h2 className="card-title text-lg text-success mb-4">
                                Dados da Autorização
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {authorization.authorizationNumber && (
                                    <div>
                                        <p className="text-xs text-base-content/60 uppercase">
                                            Número da Autorização
                                        </p>
                                        <p className="font-semibold font-mono text-lg">
                                            {authorization.authorizationNumber}
                                        </p>
                                    </div>
                                )}
                                {authorization.operatorNumber && (
                                    <div>
                                        <p className="text-xs text-base-content/60 uppercase">
                                            Número da Operadora
                                        </p>
                                        <p className="font-semibold font-mono">
                                            {authorization.operatorNumber}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

            {/* Clinical Indication */}
            {authorization.clinicalIndication && (
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body">
                        <h2 className="card-title text-lg mb-4">
                            Indicação Clínica
                        </h2>
                        <p className="p-3 bg-base-200/50 rounded-lg whitespace-pre-wrap">
                            {authorization.clinicalIndication}
                        </p>
                    </div>
                </div>
            )}

            {/* Orders */}
            <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                <div className="card-body">
                    <h2 className="card-title text-lg mb-4">
                        Procedimentos Solicitados (
                        {authorization.items?.length || 0})
                    </h2>

                    {!authorization.items || authorization.items.length === 0 ? (
                        <div className="text-center py-8 text-base-content/60">
                            <p>Nenhum procedimento</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="table table-sm">
                                <thead>
                                    <tr>
                                        <th>Código</th>
                                        <th>Procedimento</th>
                                        <th className="text-center">Qtd</th>
                                        <th className="text-center">
                                            Autorizado
                                        </th>
                                        <th>Justificativa</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {authorization.items?.map((item: any) => (
                                        <tr key={item.id}>
                                            <td className="font-mono text-sm">
                                                {item.procedureCode}
                                            </td>
                                            <td>{item.procedureName}</td>
                                            <td className="text-center">
                                                {item.requestedQuantity}
                                            </td>
                                            <td className="text-center">
                                                {item.approvedQuantity !==
                                                    undefined &&
                                                    item.approvedQuantity !==
                                                    item.requestedQuantity ? (
                                                    <span
                                                        className={`badge ${item.approvedQuantity === 0 ? 'badge-error' : 'badge-warning'}`}
                                                    >
                                                        {item.approvedQuantity}
                                                    </span>
                                                ) : (
                                                    <span className="badge badge-success">
                                                        {item.approvedQuantity ??
                                                            '-'}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="text-sm text-base-content/70">
                                                {item.notes || '-'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Denial Reason */}
            {authorization.status === 'AUTH_DENIED' &&
                authorization.denialReason && (
                    <div className="card bg-error/10 border border-error/30 shadow-sm">
                        <div className="card-body">
                            <h2 className="card-title text-lg text-error mb-4">
                                Motivo da Negativa
                            </h2>
                            <p className="p-3 bg-base-100 rounded-lg">
                                {authorization.denialReason}
                            </p>
                        </div>
                    </div>
                )}

            {/* Notes */}
            {authorization.notes && (
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body">
                        <h2 className="card-title text-lg mb-4">Observações</h2>
                        <p className="p-3 bg-base-200/50 rounded-lg">
                            {authorization.notes}
                        </p>
                    </div>
                </div>
            )}

            {/* Approve Modal */}
            {showApproveModal && (
                <div className="modal modal-open">
                    <div className="modal-box">
                        <h3 className="font-bold text-lg">
                            Aprovar Autorização
                        </h3>
                        <div className="py-4 space-y-4">
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">
                                        Número da Autorização
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered w-full"
                                    value={approveData.authorizationNumber}
                                    onChange={e =>
                                        setApproveData(prev => ({
                                            ...prev,
                                            authorizationNumber: e.target.value,
                                        }))
                                    }
                                    placeholder="Número da autorização"
                                />
                            </div>
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">
                                        Número da Operadora
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered w-full"
                                    value={approveData.operatorNumber}
                                    onChange={e =>
                                        setApproveData(prev => ({
                                            ...prev,
                                            operatorNumber: e.target.value,
                                        }))
                                    }
                                    placeholder="Número na operadora"
                                />
                            </div>
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">
                                        Validade da Autorização
                                    </span>
                                </label>
                                <input
                                    type="date"
                                    className="input input-bordered w-full"
                                    value={approveData.validUntil}
                                    onChange={e =>
                                        setApproveData(prev => ({
                                            ...prev,
                                            validUntil: e.target.value,
                                        }))
                                    }
                                />
                            </div>
                        </div>
                        <div className="modal-action">
                            <button
                                onClick={() => setShowApproveModal(false)}
                                className="btn btn-ghost"
                                disabled={actionLoading}
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleApprove}
                                className="btn btn-success"
                                disabled={actionLoading}
                            >
                                {actionLoading ? (
                                    <span className="loading loading-spinner loading-sm"></span>
                                ) : (
                                    'Aprovar'
                                )}
                            </button>
                        </div>
                    </div>
                    <div
                        className="modal-backdrop"
                        onClick={() => setShowApproveModal(false)}
                    ></div>
                </div>
            )}

            {/* Deny Modal */}
            {showDenyModal && (
                <div className="modal modal-open">
                    <div className="modal-box">
                        <h3 className="font-bold text-lg">Negar Autorização</h3>
                        <div className="py-4">
                            <label className="label">
                                <span className="label-text">
                                    Motivo da Negativa
                                </span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered w-full"
                                value={denyReason}
                                onChange={e => setDenyReason(e.target.value)}
                                placeholder="Informe o motivo (opcional)"
                                rows={3}
                            />
                        </div>
                        <div className="modal-action">
                            <button
                                onClick={() => {
                                    setShowDenyModal(false)
                                    setDenyReason('')
                                }}
                                className="btn btn-ghost"
                                disabled={actionLoading}
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleDeny}
                                className="btn btn-error"
                                disabled={actionLoading}
                            >
                                {actionLoading ? (
                                    <span className="loading loading-spinner loading-sm"></span>
                                ) : (
                                    'Negar'
                                )}
                            </button>
                        </div>
                    </div>
                    <div
                        className="modal-backdrop"
                        onClick={() => setShowDenyModal(false)}
                    ></div>
                </div>
            )}

            <ConfirmModal
                isOpen={showExpireModal}
                onClose={() => setShowExpireModal(false)}
                onConfirm={confirmExpire}
                title="Expiar autorização"
                message="Marcar autorização como expirada?"
                confirmButtonText="Expiar"
                cancelButtonText="Cancelar"
                loading={actionLoading}
            />
        </div>
    )
}
